import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface InactivityDialogProps {
  isAuthenticated: boolean;
  onLogout: () => void;
  inactivityTimeout?: number; // in milliseconds, default 15 minutes
  warningTime?: number; // in milliseconds, default 1 minute before logout
}

function InactivityDialog({
  isAuthenticated,
  onLogout,
  inactivityTimeout = 15 * 60 * 1000, // 15 minutes
  warningTime = 60 * 1000, // 1 minute warning
}: InactivityDialogProps) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    onLogout();
  }, [clearAllTimers, onLogout]);

  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;

    clearAllTimers();
    setShowWarning(false);
    setCountdown(Math.floor(warningTime / 1000));

    // Set timer to show warning
    inactivityTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(Math.floor(warningTime / 1000));

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Set timer for actual logout
      warningTimerRef.current = setTimeout(() => {
        handleLogout();
      }, warningTime);
    }, inactivityTimeout - warningTime);
  }, [isAuthenticated, inactivityTimeout, warningTime, clearAllTimers, handleLogout]);

  const handleStayLoggedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      if (!showWarning) {
        resetTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    resetTimer();

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [isAuthenticated, showWarning, resetTimer, clearAllTimers]);

  if (!isAuthenticated) return null;

  return (
    <Dialog
      open={showWarning}
      onClose={handleStayLoggedIn}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon sx={{ fontSize: 48, color: 'warning.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Session Timeout Warning
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          You've been inactive for a while. For your security, you'll be logged out soon.
        </Typography>
        <Typography variant="h4" color="warning.main" fontWeight={700}>
          {countdown}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          seconds remaining
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
        <Button
          variant="outlined"
          onClick={handleLogout}
          sx={{ minWidth: 120 }}
        >
          Logout Now
        </Button>
        <Button
          variant="contained"
          onClick={handleStayLoggedIn}
          sx={{ minWidth: 120 }}
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InactivityDialog;
